import React, { useState, useEffect, useRef, useMemo, createRef, useCallback } from 'react';
import { AnimatePresence, motion, useDragControls } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useDishStoryPosts } from '@/hooks/useDishStoryPosts';
import StoryProgressBar from './StoryProgressBar';
import StoryImageSlide from './StoryImageSlide';
import StoryVideoSlide from './StoryVideoSlide';
import StoryMediaOverlay from './StoryMediaOverlay';

const IMAGE_DURATION = 5000; // ms

interface DishStoryViewerProps {
  dishId: string | null;
  thumbnailRect: DOMRect | null;
  onClose: () => void;
}

export const DishStoryViewer: React.FC<DishStoryViewerProps> = ({
  dishId,
  thumbnailRect,
  onClose,
}) => {
  const isOpen = !!dishId;

  return (
    <AnimatePresence>
      {isOpen && dishId && (
        <StoryViewerInner
          key={dishId}
          dishId={dishId}
          thumbnailRect={thumbnailRect}
          onClose={onClose}
        />
      )}
    </AnimatePresence>
  );
};

// Inner component — only mounts when dishId is non-null, so hooks work correctly
const StoryViewerInner: React.FC<{
  dishId: string;
  thumbnailRect: DOMRect | null;
  onClose: () => void;
}> = ({ dishId, thumbnailRect, onClose }) => {
  const dragControls = useDragControls();
  const { data: posts = [], isLoading } = useDishStoryPosts(dishId);

  // Flatten posts into individual story items — one per image (or one per video)
  const storyItems = useMemo(() => posts.flatMap(post => {
    const isVideo = post.media_type === 'video' || (!!post.video_url && !post.image_url);
    if (isVideo) return [{ post, imageUrl: null as string | null }];
    const imgs = post.image_urls?.length ? post.image_urls : post.image_url ? [post.image_url] : [];
    return imgs.map(imageUrl => ({ post, imageUrl }));
  }), [posts]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(true);

  // RAF refs — no state, direct DOM writes
  const rafRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const progressRef = useRef<number>(0);

  // One ref per progress segment fill div
  const fillRefs = useMemo(
    () => Array.from({ length: Math.min(storyItems.length, 15) }, () => createRef<HTMLDivElement | null>()),
    [storyItems.length]
  );

  const activeItem = storyItems[activeIndex] ?? null;
  const activePost = activeItem?.post ?? null;
  const isVideo = activePost ? (activePost.media_type === 'video' || (!!activePost.video_url && !activePost.image_url)) : false;

  const handleNext = useCallback(() => {
    if (activeIndex >= storyItems.length - 1) {
      onClose();
      return;
    }
    setActiveIndex(i => i + 1);
  }, [activeIndex, storyItems.length, onClose]);

  const handlePrev = useCallback(() => {
    setActiveIndex(i => Math.max(0, i - 1));
  }, []);

  // Reset fill on index change
  useEffect(() => {
    if (fillRefs[activeIndex]?.current) {
      fillRefs[activeIndex].current!.style.width = '0%';
    }
    startTimeRef.current = 0;
    progressRef.current = 0;
  }, [activeIndex, fillRefs]);

  // RAF-driven progress bar for image posts
  useEffect(() => {
    if (isVideo) return; // video drives itself
    if (isPaused) {
      cancelAnimationFrame(rafRef.current);
      return;
    }

    const tick = (now: number) => {
      if (!startTimeRef.current) startTimeRef.current = now;
      const elapsed = now - startTimeRef.current;
      const p = Math.min(elapsed / IMAGE_DURATION, 1);
      progressRef.current = p;

      const fill = fillRefs[activeIndex]?.current;
      if (fill) fill.style.width = `${p * 100}%`;

      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        handleNext();
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [activeIndex, isPaused, isVideo, fillRefs, handleNext]);

  // Close when posts load empty
  useEffect(() => {
    if (!isLoading && posts.length === 0) {
      onClose();
    }
  }, [isLoading, posts.length, onClose]);

  // Compute entry animation from thumbnail position
  const initialMotion = useMemo(() => {
    if (!thumbnailRect) return { y: '100%' };
    const scaleX = thumbnailRect.width / window.innerWidth;
    const scaleY = thumbnailRect.height / window.innerHeight;
    const scale = Math.max(scaleX, scaleY);
    const x = thumbnailRect.left + thumbnailRect.width / 2 - window.innerWidth / 2;
    const y = thumbnailRect.top + thumbnailRect.height / 2 - window.innerHeight / 2;
    return { scale, x, y, opacity: 0 };
  }, [thumbnailRect]);

  const handlePause = useCallback(() => setIsPaused(true), []);
  const handleResume = useCallback(() => setIsPaused(false), []);

  return (
    <motion.div
      className="fixed inset-0 z-[200] bg-black touch-none overflow-hidden"
      initial={initialMotion}
      animate={{ scale: 1, x: 0, y: 0, opacity: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ type: 'tween', ease: 'easeOut', duration: 0.35 }}
      drag="y"
      dragControls={dragControls}
      dragListener={false}
      dragConstraints={{ top: 0 }}
      dragElastic={{ top: 0, bottom: 0.4 }}
      onDragEnd={(_, info) => {
        if (info.offset.y > 120) onClose();
      }}
    >
      {/* Drag handle at top */}
      <div
        className="absolute top-0 left-0 right-0 h-16 z-40 flex justify-center items-start pt-2"
        onPointerDown={(e) => dragControls.start(e)}
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 2px)' }}
      >
        <div className="w-10 h-1 bg-white/30 rounded-full" />
      </div>

      {/* Progress bar */}
      {storyItems.length > 0 && (
        <StoryProgressBar
          total={storyItems.length}
          activeIndex={activeIndex}
          fillRefs={fillRefs}
        />
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-white/60" />
        </div>
      )}

      {/* Active slide */}
      {activeItem && activePost && (
        <div className="absolute inset-0">
          {isVideo ? (
            <StoryVideoSlide
              post={activePost}
              isMuted={isMuted}
              onNext={handleNext}
              onPrev={handlePrev}
            />
          ) : (
            <StoryImageSlide
              post={activePost}
              imageUrl={activeItem.imageUrl ?? ''}
              onNext={handleNext}
              onPrev={handlePrev}
            />
          )}

          {/* Overlay with actions */}
          <StoryMediaOverlay
            post={activePost}
            isPaused={isPaused}
            onPause={handlePause}
            onResume={handleResume}
            isMuted={isVideo ? isMuted : undefined}
            onToggleMute={isVideo ? () => setIsMuted(m => !m) : undefined}
          />
        </div>
      )}

      {/* Preload next slide (hidden) */}
      {storyItems[activeIndex + 1] && (() => {
        const next = storyItems[activeIndex + 1];
        const nextIsVideo = next.post.media_type === 'video' || (!!next.post.video_url && !next.post.image_url);
        if (nextIsVideo) {
          const url = next.post.video_urls?.[0] ?? next.post.video_url;
          if (url) return <link key={`${next.post.id}-preload`} rel="preload" as="video" href={url} />;
        } else {
          const url = next.imageUrl;
          if (url) return <link key={`${next.post.id}-${url}`} rel="preload" as="image" href={url} />;
        }
        return null;
      })()}
    </motion.div>
  );
};
